import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import reportIcon from 'assets/images/icons/SprengnetterAppsReport.png'
import Button from 'components/Button/Button'
import { StyledContainer } from '../../ProductCheckout/ProductCheckout.styled'
import { useTranslation } from 'react-i18next'
import { selectCustomerConfiguration } from 'slices/customerConfigurationSlice'
import { selectCustomerCoins, selectProductsCost } from 'slices/coinsSlice'
import { useLazyGetCustomerCoinsQuery } from 'services/coins-api'
import NoCoinsContainer from '../../ProductCheckout/NoCoinsContainer/NoCoinsContainer'
import { useLazyGetReportDocumentQuery } from 'services/report-api'
import { selectAddressFormData } from 'slices/addressFormSlice'
import { useToastEffect } from 'components/Toast/hooks/useToastEffect'
import ProductGenerationError from 'components/ProductGenerationError/ProductGenerationError'
import ProductGenerationSuccess from 'components/ProductGenerationSuccess/ProductGenerationSuccess'
import { findInArray, isProductBilled } from 'common/utils/generic'

export interface ReportCheckoutProps {
  isReportGenerated: boolean
  setReportGenerated: Dispatch<SetStateAction<boolean>>
  onBackdropClick: () => void
}

const ReportCheckout = (props: ReportCheckoutProps) => {
  const { isReportGenerated, setReportGenerated, onBackdropClick } = props
  const { t } = useTranslation()
  const addressFormData = useSelector(selectAddressFormData)
  const servicePrices = useSelector(selectProductsCost)
  const customerConfig = useSelector(selectCustomerConfiguration)
  const customerCoins = useSelector(selectCustomerCoins)
  const premiumCoins =
    customerCoins.coins?.accounts?.find((item) => item.type === 'on-demand')?.amount || 0
  const subscriptionCoinsData = customerCoins.coins?.accounts?.find(
    (item) => item.type === 'subscription'
  )
  const subscriptionCoins = subscriptionCoinsData?.amount
  const reportProduct = findInArray(customerConfig?.products, 'name', 'report')
  const isReportBilled = isProductBilled(reportProduct, customerConfig)
  const hasEnoughCoins =
    (premiumCoins || subscriptionCoins) &&
    (servicePrices?.report <= subscriptionCoins || servicePrices?.report <= premiumCoins)
  const [showToastError, setShowToastError] = useState(false)

  const [getCustomerCoins] = useLazyGetCustomerCoinsQuery()

  const [getReportDocument, { data, isFetching, isSuccess, isError }] =
    useLazyGetReportDocumentQuery()

  const handleDownloadButtonClick = () => {
    if (isFetching) return
    getReportDocument({ id: addressFormData.addressData.id, format: 'pdf' })
  }

  useEffect(() => {
    if (isSuccess) {
      const anchor = document.createElement('a')
      const fileName = `report_${addressFormData.addressData.zip}-${addressFormData.addressData.town}`
      anchor.setAttribute('download', fileName)
      document.body.appendChild(anchor)
      const objectUrl = window.URL.createObjectURL(data)
      anchor.href = objectUrl
      anchor.click()

      window.URL.revokeObjectURL(objectUrl)
      document.body.removeChild(anchor)
    }
  }, [isSuccess, data, addressFormData])

  useEffect(() => {
    if (isSuccess) {
      getCustomerCoins()
      setShowToastError(false)
    }
    if (isSuccess || isError) {
      setReportGenerated(true)
    }
  }, [isSuccess, isError, getCustomerCoins, setReportGenerated])

  useToastEffect(t('newAddressModal.finishTab.reportInfo.error.toast'), 'ERROR', [
    isError,
    showToastError,
  ])

  return (
    <>
      {!isReportGenerated &&
        (!isReportBilled || hasEnoughCoins ? (
          <StyledContainer>
            <img src={reportIcon} alt="" className="app-icon" />
            <h1>{t('common.generateReport')}</h1>
            {isReportBilled && (
              <p>
                {t('newAddressModal.finishTab.reportInfo.description', {
                  value: servicePrices?.report,
                })}
              </p>
            )}
            <div onClick={() => handleDownloadButtonClick()}>
              <Button kind="primary" loading={isFetching} disabled={isFetching}>
                {isFetching ? t('common.loadingButton') : t('common.generateReport')}
              </Button>
            </div>
          </StyledContainer>
        ) : (
          <NoCoinsContainer
            icon={reportIcon}
            descriptionKey="newAddressModal.finishTab.reportInfo.noCoinsDescription"
            buttonLabel={t('common.generateReport')}
            price={servicePrices?.report}
          />
        ))}
      {isSuccess && (
        <ProductGenerationSuccess
          icon={reportIcon}
          title={t('newAddressModal.finishTab.reportInfo.downloadTitle')}
          message1={t('newAddressModal.finishTab.reportInfo.downloadDescription')}
          message2={t('newAddressModal.finishTab.reportInfo.successDescription')}
        >
          <Button kind="secondary" onClick={onBackdropClick}>
            {t('common.closeButton')}
          </Button>
        </ProductGenerationSuccess>
      )}
      {(isError || showToastError) && (
        <ProductGenerationError
          icon={reportIcon}
          title={t('newAddressModal.finishTab.reportInfo.errorTitle')}
          message1={t('newAddressModal.finishTab.reportInfo.errorDescription.1')}
          message2={t('newAddressModal.finishTab.reportInfo.errorDescription.2')}
          isLoading={isFetching}
          tryAgainAction={() => {
            setShowToastError(true)
            handleDownloadButtonClick()
          }}
        />
      )}
    </>
  )
}

export default ReportCheckout
